import { ArrowUpRight, Bell, ChevronDown, CircleUserRound, House, Menu, Sparkles, Store, UsersRound, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import logoDark from '../../assets/logo-simple-dark.svg';
import logoWhite from '../../assets/logo-simple-white.svg';
import { useAuth } from '../../hooks/useAuth';

const platformItems = [
  { label: 'Overview', to: '/platform' },
  { label: 'Admissions & Enrollment', to: '/platform/admissions' },
  { label: 'Live Classes', to: '/platform/learning-live-classes' },
  { label: 'Learning Management', to: '/platform/lms' },
  { label: 'Payments & Finance', to: '/platform/fees-receipts' },
  { label: 'Communication', to: '/platform/communication' },
  { label: 'HR & Staff', to: '/platform/hr-staff' },
  { label: 'Campus Operations', to: '/platform/campus-operations' },
  { label: 'SmartCampus Firefly', to: '/platform/firefly' },
  { label: 'Site Builder', to: '/platform/site-builder' },
];

const audienceItems = [
  { label: 'Private Tutors', to: '/who-its-for/private-tutors' },
  { label: 'Schools & Academies', to: '/who-its-for/schools-academies' },
  { label: 'Training Centers', to: '/who-its-for/training-centers' },
  { label: 'Colleges & Universities', to: '/who-its-for/colleges-universities' },
  { label: 'Corporate Training', to: '/who-its-for/corporate-training' },
  { label: 'Education Businesses', to: '/who-its-for/education-businesses' },
];
const menuItems = [
  { label: 'About Us', to: '/about-us' },
  { label: 'Contact Us', to: '/contact-us' },
  { label: 'Terms of Service', to: '/terms-of-service' },
  { label: 'Data Processing Addendum', to: '/data-processing-addendum' },
];
const socialLinks = [
  { label: 'LinkedIn', href: 'https://www.linkedin.com', icon: 'linkedin' },
  { label: 'Instagram', href: 'https://www.instagram.com', icon: 'instagram' },
  { label: 'TikTok', href: 'https://www.tiktok.com', icon: 'tiktok' },
  { label: 'Facebook', href: 'https://www.facebook.com', icon: 'facebook' },
];

export function SocialIcon({ icon }) {
  const paths = {
    linkedin: <path d="M5.5 3.75a1.75 1.75 0 1 1 0 3.5 1.75 1.75 0 0 1 0-3.5ZM4 8.5h3v11H4v-11Zm5.25 0h2.87v1.5h.04c.4-.76 1.38-1.75 3.4-1.75 3.1 0 3.99 1.96 3.99 4.92v6.33h-3v-5.62c0-1.34-.02-3.07-1.87-3.07-1.87 0-2.16 1.46-2.16 2.97v5.72h-3.27v-11Z" fill="currentColor" />,
    tiktok: <><path d="M13.4 3.5c.8 1.7 2.2 2.8 4 3.1v2.9c-1.4 0-2.7-.4-3.9-1.2v5.2c0 3.4-2.7 6.2-6.1 6.2S1.3 17 1.3 13.6c0-3.2 2.4-5.9 5.5-6.2v3c-1.4.3-2.5 1.5-2.5 3.1 0 1.8 1.5 3.2 3.2 3.2s3.2-1.4 3.2-3.2V3.5h2.7Z" fill="currentColor" /><path d="M13.4 3.5c.8 1.7 2.2 2.8 4 3.1v2.1c-1.7-.2-3.1-.9-4.2-2V3.5h.2Z" fill="currentColor" opacity=".35" /></>,
    facebook: <path d="M14.1 8.1V6.9c0-.8.5-1.2 1.3-1.2h1.7V3h-2.4c-2.6 0-4.2 1.5-4.2 4.1v1H8v2.7h2.5V21h3.6v-10.2h2.7l.4-2.7h-3.1Z" fill="currentColor" />,
    whatsapp: <path d="M12 3.5A8.5 8.5 0 0 0 4 15.1L3 20l4.96-1.03A8.5 8.5 0 1 0 12 3.5Zm4.94 12.13c-.2.56-1.18 1.07-1.62 1.12-.43.05-.93.07-1.5-.11-.35-.11-.8-.26-1.38-.51-2.43-1.05-4.02-3.55-4.14-3.72-.11-.17-.99-1.32-.99-2.51 0-1.19.62-1.76.85-2 .23-.24.5-.3.67-.3h.48c.15 0 .35-.06.55.43.2.5.67 1.72.73 1.85.06.13.1.28.02.45-.08.17-.12.28-.24.43-.11.14-.23.3-.33.4-.11.1-.22.21-.09.43.12.22.57.94 1.23 1.52.85.77 1.56 1.01 1.8 1.12.24.1.38.09.52-.05.14-.14.6-.7.76-.94.17-.24.33-.2.55-.12.22.09 1.4.66 1.64.78.24.13.4.2.46.31.07.11.07.64-.13 1.2Z" fill="currentColor" />,
  };
  if (icon === 'instagram') return <svg aria-hidden="true" viewBox="0 0 24 24"><rect x="3" y="3" width="18" height="18" rx="5" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round" /><circle cx="12" cy="12" r="3.9" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round" /><circle cx="17.2" cy="6.8" r="1.15" fill="currentColor" /></svg>;
  return <svg aria-hidden="true" viewBox="0 0 24 24">{paths[icon]}</svg>;
}

export function SmartCampusNavigation({ light = false }) {
  const { authenticated } = useAuth();
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [mobileSectionOpen, setMobileSectionOpen] = useState(null);
  const closeDropdown = () => {
    setActiveDropdown(null);
    setMobileSectionOpen(null);
  };

  useEffect(() => {
    const handleEscape = (event) => { if (event.key === 'Escape') closeDropdown(); };
    if (activeDropdown === 'menu') {
      document.body.style.overflow = 'hidden';
      window.addEventListener('keydown', handleEscape);
    }
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleEscape);
    };
  }, [activeDropdown]);

  const renderMobileSection = (label, key, items) => (
    <section>
      <button className={`tennis-drawer-section-toggle${mobileSectionOpen === key ? ' tennis-drawer-section-toggle--open' : ''}`} type="button" aria-expanded={mobileSectionOpen === key} onClick={() => setMobileSectionOpen((current) => (current === key ? null : key))}>
        <span>{label}</span><ChevronDown size={17} />
      </button>
      {mobileSectionOpen === key ? <div className="tennis-drawer-section-links">{items.map(({ label: itemLabel, to }) => <Link to={to} key={itemLabel} onClick={closeDropdown}><span>{itemLabel}</span><ArrowUpRight size={18} /></Link>)}</div> : null}
    </section>
  );

  const fireflyTarget = '/platform/firefly';
  return (
    <>
      <header className={`tennis-header${light ? ' tennis-header--light' : ''}`}>
        <Link className="tennis-logo" to="/" aria-label="SmartCampus home">
          <img src={light ? logoDark : logoWhite} alt="" aria-hidden="true" />
        </Link>
        <nav className="tennis-nav" aria-label="Main navigation">
          <a href="/" onClick={closeDropdown}><House size={22} strokeWidth={1.8} /><span>Home</span></a>
          <div className={`tennis-nav-item tennis-nav-item--dropdown${activeDropdown === 'platform' ? ' tennis-nav-item--open' : ''}`}>
            <button aria-expanded={activeDropdown === 'platform'} className="tennis-nav-trigger" onClick={() => setActiveDropdown((current) => (current === 'platform' ? null : 'platform'))} type="button"><Store size={22} strokeWidth={1.8} /><span>Platform</span><ChevronDown size={16} /></button>
            {activeDropdown === 'platform' && <div className="tennis-dropdown tennis-dropdown--open" role="menu" aria-label="Platform menu">{platformItems.map(({ label, to }) => <Link key={label} to={to} role="menuitem" onClick={closeDropdown}>{label}</Link>)}</div>}
          </div>
          <div className={`tennis-nav-item tennis-nav-item--dropdown${activeDropdown === 'audience' ? ' tennis-nav-item--open' : ''}`}>
            <button aria-expanded={activeDropdown === 'audience'} className="tennis-nav-trigger" onClick={() => setActiveDropdown((current) => (current === 'audience' ? null : 'audience'))} type="button"><UsersRound size={22} strokeWidth={1.8} /><span>Who It&apos;s For</span><ChevronDown size={16} /></button>
            {activeDropdown === 'audience' && <div className="tennis-dropdown tennis-dropdown--open" role="menu" aria-label="Who it is for menu">{audienceItems.map(({ label, to }) => <Link key={label} to={to} role="menuitem" onClick={closeDropdown}>{label}</Link>)}</div>}
          </div>
          <Link to={fireflyTarget} onClick={closeDropdown}><Sparkles size={22} strokeWidth={1.8} /><span>Firefly</span></Link>
        </nav>
        <div className="tennis-header-actions">
          <div className={`tennis-header-menu${activeDropdown === 'menu' ? ' tennis-header-menu--open' : ''}`}>
            <button aria-expanded={activeDropdown === 'menu'} aria-haspopup="menu" className="tennis-menu-button" onClick={() => setActiveDropdown((current) => (current === 'menu' ? null : 'menu'))} type="button"><span>Menu</span><Menu size={25} /></button>
          </div>
          <Link className="tennis-bag-button" to="/news-and-updates" aria-label="News and updates" onClick={closeDropdown}><Bell size={21} /></Link>
          <Link className="tennis-member-button" to={authenticated ? '/dashboard' : '/register'} onClick={closeDropdown}><CircleUserRound size={19} />{authenticated ? 'Dashboard' : 'Get started'}</Link>
        </div>
      </header>
      {activeDropdown === 'menu' && (
        <div className="tennis-menu-overlay" role="presentation">
          <button className="tennis-menu-backdrop" type="button" aria-label="Close menu" onClick={closeDropdown} />
          <aside className="tennis-menu-drawer" aria-label="Site menu" aria-modal="true" role="dialog">
            <header className="tennis-drawer-header"><Link className="tennis-drawer-logo" to="/" onClick={closeDropdown} aria-label="SmartCampus home"><img src={logoDark} alt="" aria-hidden="true" /></Link><button className="tennis-drawer-close" type="button" aria-label="Close menu" onClick={closeDropdown}><X size={23} /></button></header>
            <div className="tennis-drawer-intro"><h2>Elevate your<br />student experience.</h2></div>
            <nav className="tennis-drawer-links" aria-label="Site links">{menuItems.map(({ label, to }) => <Link to={to} key={label} onClick={closeDropdown}><span>{label}</span><ArrowUpRight size={20} /></Link>)}</nav>
            <Link className="tennis-drawer-home-link" to="/" onClick={closeDropdown}><span>Home</span><ArrowUpRight size={18} /></Link>
            <div className="tennis-drawer-mobile-sections">
              {renderMobileSection('Platform', 'platform', platformItems)}
              {renderMobileSection("Who it's for", 'audience', audienceItems)}
              {renderMobileSection('Company & support', 'company', menuItems)}
            </div>
            <div className="tennis-drawer-footer"><Link className="tennis-drawer-cta-link" to="/register" onClick={closeDropdown}>Start building <ArrowUpRight size={17} /></Link><div className="tennis-socials" aria-label="Social links">{socialLinks.map(({ label, href, icon }) => <a className="tennis-social-link" href={href} key={label} rel="noreferrer noopener" target="_blank" aria-label={label} title={label}><SocialIcon icon={icon} /></a>)}</div></div>
          </aside>
        </div>
      )}
    </>
  );
}
